import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ChatHiddenContext, GraphActionProposal } from 'src/app/services/chat/chat.service';
import { ITab } from 'src/app/model/tab';
import { IThingie } from 'src/app/model/thingie/thingie';
import { TabStorageService } from '../tab-storage/tab-storage.service';
import { ThingieService } from '../thingie/thingie-service.service';
import { createDefaultTab } from 'src/app/utility/default-object-creator';
import { deepCopySync } from 'src/app/utility/deep-copy';
import { generateNewTabUUID } from 'src/app/utility/uuid-generator';
import { GraphAgentValidationAdapterService } from './graph-agent-validation-adapter.service';
import { GraphAgentContextService } from './graph-agent-context.service';
import { IDataSeries } from 'src/app/model/data-series';
import { GraphAgentEventsService } from './graph-agent-events.service';

export interface GraphActionExecutionResult {
  ok: boolean;
  errors?: string[];
  savedLabel?: string;
  selectedGraphConfigUuid?: string;
}

@Injectable({ providedIn: 'root' })
export class GraphActionExecutorService {
  constructor(
    private graphAgentContextService: GraphAgentContextService,
    private graphAgentEventsService: GraphAgentEventsService,
    private tabStorage: TabStorageService,
    private thingieService: ThingieService,
    private validationAdapter: GraphAgentValidationAdapterService
  ) {}

  public async applyProposal(
    proposal: GraphActionProposal,
    hiddenContext?: ChatHiddenContext
  ): Promise<GraphActionExecutionResult> {
    const thingieId = hiddenContext?.graphAgentContext?.thingieId?.trim();
    if (!thingieId) {
      return { ok: false, errors: ['Missing graph context for this action.'] };
    }

    if (proposal.action === 'selectGraphTab') {
      return this.applySelectGraphTab(proposal, thingieId);
    }

    const thingie = await firstValueFrom(this.thingieService.getThingieById(thingieId, true));
    const visibleSeries = await this.graphAgentContextService.getSelectorVisibleSeriesForThingie(thingie);
    const tabs = (await this.tabStorage.loadCurrentUserTabConfigurations({ thingieId, dashboardCard: false })) ?? [];

    const actionResult = this.applyActionToTabs(proposal, thingie, tabs, visibleSeries);
    if (!actionResult.ok) {
      return actionResult;
    }

    const validationErrors = this.validationAdapter.validateTabs(actionResult.tabs);
    if (validationErrors.length > 0) {
      return { ok: false, errors: validationErrors };
    }

    const savedTabs = await this.tabStorage.saveCurrentUserTabConfigurations(
      { thingieId, dashboardCard: false },
      actionResult.tabs
    );

    await this.tabStorage.saveLastViewedTabForUser({
      thingieId,
      tab: actionResult.selectedTabIndex
    });

    this.graphAgentEventsService.notifyGraphTabsUpdated({
      thingieId,
      selectedGraphConfigUuid: actionResult.selectedGraphConfigUuid
    });

    return {
      ok: true,
      savedLabel: savedTabs[actionResult.selectedTabIndex]?.graphConfig.label ?? actionResult.label,
      selectedGraphConfigUuid: actionResult.selectedGraphConfigUuid
    };
  }

  private applyActionToTabs(
    proposal: GraphActionProposal,
    thingie: IThingie,
    currentTabs: ITab[],
    visibleSeries: IDataSeries[]
  ): GraphActionExecutionResult & { tabs: ITab[]; selectedTabIndex: number; label?: string; selectedGraphConfigUuid?: string } {
    const tabs = deepCopySync(currentTabs);
    switch (proposal.action) {
      case 'createGraphTab':
        return this.createGraphTab(proposal, thingie, tabs, visibleSeries);
      case 'duplicateAndModifyGraphTab':
        return this.duplicateAndModifyGraphTab(proposal, thingie, tabs, visibleSeries);
      case 'addSeriesToGraph':
        return this.addSeriesToGraph(proposal, tabs, visibleSeries);
      case 'removeSeriesFromGraph':
        return this.removeSeriesFromGraph(proposal, tabs, visibleSeries);
      case 'renameGraphTab':
        return this.renameGraphTab(proposal, tabs);
      case 'replaceGraphTabSeries':
        return this.replaceGraphTabSeries(proposal, tabs, visibleSeries);
      case 'selectGraphTab':
        return { ok: false, errors: ['selectGraphTab is handled separately.'], tabs, selectedTabIndex: 0 };
    }
  }

  private createGraphTab(
    proposal: GraphActionProposal,
    thingie: IThingie,
    tabs: ITab[],
    visibleSeries: IDataSeries[]
  ): GraphActionExecutionResult & { tabs: ITab[]; selectedTabIndex: number; label?: string; selectedGraphConfigUuid?: string } {
    const seriesResolution = this.resolveSeries(proposal, visibleSeries, []);
    if (!seriesResolution.ok) {
      return { ...seriesResolution, tabs, selectedTabIndex: tabs.length };
    }

    const newTab = createDefaultTab();
    newTab.graphConfig.uuid = generateNewTabUUID();
    newTab.graphConfig.thingie = thingie._id;
    newTab.graphConfig.label = this.ensureUniqueLabel(
      (proposal.label?.trim() || this.buildDefaultLabel(seriesResolution.series)),
      tabs
    );
    newTab.graphConfig.timeReference = proposal.timeReference ?? newTab.graphConfig.timeReference;
    newTab.graphConfig.internalSeriesList = seriesResolution.series.map(series => {
      const clonedSeries = deepCopySync(series);
      clonedSeries.observed = true;
      return clonedSeries;
    });

    tabs.push(newTab);

    return {
      ok: true,
      tabs,
      selectedTabIndex: tabs.length - 1,
      label: newTab.graphConfig.label,
      selectedGraphConfigUuid: newTab.graphConfig.uuid
    };
  }

  private duplicateAndModifyGraphTab(
    proposal: GraphActionProposal,
    thingie: IThingie,
    tabs: ITab[],
    visibleSeries: IDataSeries[]
  ): GraphActionExecutionResult & { tabs: ITab[]; selectedTabIndex: number; label?: string; selectedGraphConfigUuid?: string } {
    if (!proposal.sourceTabUuid) {
      return { ok: false, errors: ['A source graph is required for duplication.'], tabs, selectedTabIndex: 0 };
    }

    const sourceTab = tabs.find(tab => tab.graphConfig.uuid === proposal.sourceTabUuid);
    if (!sourceTab) {
      return { ok: false, errors: ['The source graph could not be found.'], tabs, selectedTabIndex: 0 };
    }

    const newTab = deepCopySync(sourceTab);
    newTab.backendId = null;
    newTab.graphConfig.uuid = generateNewTabUUID();
    newTab.graphConfig.thingie = thingie._id;
    newTab.graphConfig.label = this.ensureUniqueLabel(
      proposal.label?.trim() || `${sourceTab.graphConfig.label} Copy`,
      tabs
    );

    if (proposal.timeReference) {
      newTab.graphConfig.timeReference = proposal.timeReference;
    }

    if ((proposal.seriesKeys?.length ?? 0) > 0 || (proposal.seriesNames?.length ?? 0) > 0) {
      const seriesResolution = this.resolveSeries(proposal, visibleSeries, sourceTab.graphConfig.internalSeriesList);
      if (!seriesResolution.ok) {
        return { ...seriesResolution, tabs, selectedTabIndex: 0 };
      }

      const mergedSeries = [...newTab.graphConfig.internalSeriesList];
      for (const series of seriesResolution.series) {
        if (mergedSeries.some(existing => this.getSeriesKey(existing) === this.getSeriesKey(series))) {
          continue;
        }
        const clonedSeries = deepCopySync(series);
        clonedSeries.observed = true;
        mergedSeries.push(clonedSeries);
      }
      newTab.graphConfig.internalSeriesList = mergedSeries;
    }

    tabs.push(newTab);

    return {
      ok: true,
      tabs,
      selectedTabIndex: tabs.length - 1,
      label: newTab.graphConfig.label,
      selectedGraphConfigUuid: newTab.graphConfig.uuid
    };
  }

  private addSeriesToGraph(
    proposal: GraphActionProposal,
    tabs: ITab[],
    visibleSeries: IDataSeries[]
  ): GraphActionExecutionResult & { tabs: ITab[]; selectedTabIndex: number; label?: string; selectedGraphConfigUuid?: string } {
    if (!proposal.sourceTabUuid) {
      return { ok: false, errors: ['A target graph is required to add series.'], tabs, selectedTabIndex: 0 };
    }

    const tabIndex = tabs.findIndex(tab => tab.graphConfig.uuid === proposal.sourceTabUuid);
    if (tabIndex === -1) {
      return { ok: false, errors: ['The target graph could not be found.'], tabs, selectedTabIndex: 0 };
    }

    const tab = tabs[tabIndex];
    const seriesResolution = this.resolveSeries(proposal, visibleSeries, tab.graphConfig.internalSeriesList);
    if (!seriesResolution.ok) {
      return { ...seriesResolution, tabs, selectedTabIndex: tabIndex };
    }

    for (const series of seriesResolution.series) {
      if (tab.graphConfig.internalSeriesList.some(existing => this.getSeriesKey(existing) === this.getSeriesKey(series))) {
        continue;
      }
      const clonedSeries = deepCopySync(series);
      clonedSeries.observed = true;
      tab.graphConfig.internalSeriesList.push(clonedSeries);
    }

    if (proposal.label?.trim()) {
      tab.graphConfig.label = this.ensureUniqueLabel(proposal.label.trim(), tabs, tab.graphConfig.uuid);
    }
    if (proposal.timeReference) {
      tab.graphConfig.timeReference = proposal.timeReference;
    }

    return {
      ok: true,
      tabs,
      selectedTabIndex: tabIndex,
      label: tab.graphConfig.label,
      selectedGraphConfigUuid: tab.graphConfig.uuid
    };
  }

  private removeSeriesFromGraph(
    proposal: GraphActionProposal,
    tabs: ITab[],
    visibleSeries: IDataSeries[]
  ): GraphActionExecutionResult & { tabs: ITab[]; selectedTabIndex: number; label?: string; selectedGraphConfigUuid?: string } {
    if (!proposal.sourceTabUuid) {
      return { ok: false, errors: ['A target graph is required to remove series.'], tabs, selectedTabIndex: 0 };
    }

    const tabIndex = tabs.findIndex(tab => tab.graphConfig.uuid === proposal.sourceTabUuid);
    if (tabIndex === -1) {
      return { ok: false, errors: ['The target graph could not be found.'], tabs, selectedTabIndex: 0 };
    }

    const tab = tabs[tabIndex];
    const seriesResolution = this.resolveSeries(proposal, visibleSeries, tab.graphConfig.internalSeriesList);
    if (!seriesResolution.ok) {
      return { ...seriesResolution, tabs, selectedTabIndex: tabIndex };
    }

    const keysToRemove = new Set(seriesResolution.series.map(s => this.getSeriesKey(s)));
    const remainingSeries = tab.graphConfig.internalSeriesList.filter(
      existing => !keysToRemove.has(this.getSeriesKey(existing))
    );

    if (remainingSeries.length === 0) {
      return { ok: false, errors: ['Cannot remove all series from a graph. At least one series must remain.'], tabs, selectedTabIndex: tabIndex };
    }

    tab.graphConfig.internalSeriesList = remainingSeries;

    return {
      ok: true,
      tabs,
      selectedTabIndex: tabIndex,
      label: tab.graphConfig.label,
      selectedGraphConfigUuid: tab.graphConfig.uuid
    };
  }

  private replaceGraphTabSeries(
    proposal: GraphActionProposal,
    tabs: ITab[],
    visibleSeries: IDataSeries[]
  ): GraphActionExecutionResult & { tabs: ITab[]; selectedTabIndex: number; label?: string; selectedGraphConfigUuid?: string } {
    if (!proposal.sourceTabUuid) {
      return { ok: false, errors: ['A target graph is required to replace series.'], tabs, selectedTabIndex: 0 };
    }

    const tabIndex = tabs.findIndex(tab => tab.graphConfig.uuid === proposal.sourceTabUuid);
    if (tabIndex === -1) {
      return { ok: false, errors: ['The target graph could not be found.'], tabs, selectedTabIndex: 0 };
    }

    const tab = tabs[tabIndex];
    const seriesResolution = this.resolveSeries(proposal, visibleSeries, tab.graphConfig.internalSeriesList);
    if (!seriesResolution.ok) {
      return { ...seriesResolution, tabs, selectedTabIndex: tabIndex };
    }

    tab.graphConfig.internalSeriesList = seriesResolution.series.map(series => {
      const clonedSeries = deepCopySync(series);
      clonedSeries.observed = true;
      return clonedSeries;
    });

    if (proposal.label?.trim()) {
      tab.graphConfig.label = this.ensureUniqueLabel(proposal.label.trim(), tabs, tab.graphConfig.uuid);
    }
    if (proposal.timeReference) {
      tab.graphConfig.timeReference = proposal.timeReference;
    }

    return {
      ok: true,
      tabs,
      selectedTabIndex: tabIndex,
      label: tab.graphConfig.label,
      selectedGraphConfigUuid: tab.graphConfig.uuid
    };
  }

  private renameGraphTab(
    proposal: GraphActionProposal,
    tabs: ITab[]
  ): GraphActionExecutionResult & { tabs: ITab[]; selectedTabIndex: number; label?: string; selectedGraphConfigUuid?: string } {
    if (!proposal.sourceTabUuid) {
      return { ok: false, errors: ['A target graph is required for renaming.'], tabs, selectedTabIndex: 0 };
    }

    const tabIndex = tabs.findIndex(tab => tab.graphConfig.uuid === proposal.sourceTabUuid);
    if (tabIndex === -1) {
      return { ok: false, errors: ['The target graph could not be found.'], tabs, selectedTabIndex: 0 };
    }

    const newLabel = proposal.newLabel?.trim();
    if (!newLabel) {
      return { ok: false, errors: ['A new label is required for renaming.'], tabs, selectedTabIndex: tabIndex };
    }

    const tab = tabs[tabIndex];
    tab.graphConfig.label = this.ensureUniqueLabel(newLabel, tabs, tab.graphConfig.uuid);

    return {
      ok: true,
      tabs,
      selectedTabIndex: tabIndex,
      label: tab.graphConfig.label,
      selectedGraphConfigUuid: tab.graphConfig.uuid
    };
  }

  private async applySelectGraphTab(
    proposal: GraphActionProposal,
    thingieId: string
  ): Promise<GraphActionExecutionResult> {
    if (!proposal.sourceTabUuid) {
      return { ok: false, errors: ['A target graph is required for selection.'] };
    }

    const tabs = (await this.tabStorage.loadCurrentUserTabConfigurations({ thingieId, dashboardCard: false })) ?? [];
    const tabIndex = tabs.findIndex(tab => tab.graphConfig.uuid === proposal.sourceTabUuid);
    if (tabIndex === -1) {
      return { ok: false, errors: ['The target graph could not be found.'] };
    }

    await this.tabStorage.saveLastViewedTabForUser({ thingieId, tab: tabIndex });

    this.graphAgentEventsService.notifyGraphTabsUpdated({
      thingieId,
      selectedGraphConfigUuid: proposal.sourceTabUuid
    });

    return {
      ok: true,
      savedLabel: tabs[tabIndex].graphConfig.label,
      selectedGraphConfigUuid: proposal.sourceTabUuid
    };
  }

  private resolveSeries(
    proposal: GraphActionProposal,
    visibleSeries: IDataSeries[],
    anchorSeries: IDataSeries[]
  ) {
    const resolvedSeries: IDataSeries[] = [];
    const availableByKey = new Map(visibleSeries.map(series => [this.getSeriesKey(series), series]));
    const availableByName = new Map<string, IDataSeries[]>();
    for (const series of visibleSeries) {
      const name = this.getSeriesDisplayName(series).toLowerCase();
      const existing = availableByName.get(name) ?? [];
      existing.push(series);
      availableByName.set(name, existing);
    }

    for (const key of proposal.seriesKeys ?? []) {
      const series = availableByKey.get(key);
      if (!series) {
        return { ok: false as const, errors: [`Series "${key}" is not visible in the series selector.`] };
      }
      resolvedSeries.push(series);
    }

    const sortedAnchorSeries = this.sortSeriesCandidates(anchorSeries);
    const uniqueMatches: IDataSeries[] = [];
    const ambiguousNameMatches = new Map<string, IDataSeries[]>();

    for (const name of proposal.seriesNames ?? []) {
      const matches = availableByName.get(name.trim().toLowerCase()) ?? [];
      if (matches.length === 0) {
        return { ok: false as const, errors: [`Series "${name}" is not visible in the series selector.`] };
      }
      const sortedMatches = this.sortSeriesCandidates(matches);
      if (sortedMatches.length === 1) {
        uniqueMatches.push(sortedMatches[0]);
        continue;
      }
      ambiguousNameMatches.set(name, sortedMatches);
    }

    for (const series of uniqueMatches) {
      resolvedSeries.push(series);
    }

    for (const [name, matches] of ambiguousNameMatches.entries()) {
      const chosenSeries = this.pickPreferredSeries(matches, [...sortedAnchorSeries, ...resolvedSeries]);
      if (!chosenSeries) {
        return { ok: false as const, errors: [`Series "${name}" is ambiguous. Please choose a more specific series.`] };
      }
      resolvedSeries.push(chosenSeries);
    }

    if (resolvedSeries.length === 0) {
      return { ok: false as const, errors: ['No visible series were provided for this graph action.'] };
    }

    return { ok: true as const, series: this.uniqueSeries(resolvedSeries) };
  }

  private pickPreferredSeries(candidates: IDataSeries[], anchors: IDataSeries[]): IDataSeries | undefined {
    const uniqueAnchors = this.uniqueSeries(anchors);
    if (uniqueAnchors.length === 0) {
      return candidates[0];
    }

    const anchorTaskKeys = uniqueAnchors
      .map(series => this.getTaskAnchorKey(series))
      .filter((anchorKey): anchorKey is string => !!anchorKey);

    if (anchorTaskKeys.length === 0) {
      return candidates[0];
    }

    const sameTaskMatch = candidates.find(candidate => {
      const candidateTaskKey = this.getTaskAnchorKey(candidate);
      return !!candidateTaskKey && anchorTaskKeys.includes(candidateTaskKey);
    });

    return sameTaskMatch ?? candidates[0];
  }

  private uniqueSeries(seriesList: IDataSeries[]) {
    const seen = new Set<string>();
    return seriesList.filter(series => {
      const key = this.getSeriesKey(series);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  private buildDefaultLabel(seriesList: IDataSeries[]): string {
    const firstSeries = seriesList[0];
    return `${this.getSeriesDisplayName(firstSeries)} Graph`;
  }

  private ensureUniqueLabel(label: string, tabs: ITab[], currentUuid?: string): string {
    const trimmed = label.trim() || 'New graph';
    const otherLabels = new Set(
      tabs
        .filter(tab => tab.graphConfig.uuid !== currentUuid)
        .map(tab => tab.graphConfig.label.trim().toLowerCase())
    );

    if (!otherLabels.has(trimmed.toLowerCase())) {
      return trimmed;
    }

    let counter = 2;
    while (otherLabels.has(`${trimmed} ${counter}`.toLowerCase())) {
      counter++;
    }

    return `${trimmed} ${counter}`;
  }

  private getSeriesDisplayName(series: IDataSeries): string {
    switch (series.identification.type) {
      case 'thingieSeries':
        return series.identification.dimensionName || series.identification.output;
      case 'deviceSeries':
        return series.dataItemDescription[series.identification.property]?.valueInfo?.[0]?.name ?? series.identification.property;
      case 'annotationSeries':
        return series.identification.systemAnnotation ? 'System annotations' : 'User annotations';
    }
  }

  private sortSeriesCandidates(seriesList: IDataSeries[]): IDataSeries[] {
    return [...seriesList].sort((left, right) => {
      const leftType = left.identification.type;
      const rightType = right.identification.type;
      if (leftType !== rightType) {
        return leftType.localeCompare(rightType);
      }

      switch (left.identification.type) {
        case 'thingieSeries': {
          if (right.identification.type !== 'thingieSeries') {
            return 0;
          }
          return (
            (left.identification.thingieId ?? '').localeCompare(right.identification.thingieId ?? '')
            || left.identification.processIndex - right.identification.processIndex
            || left.identification.taskIndex - right.identification.taskIndex
            || left.identification.output.localeCompare(right.identification.output)
          );
        }
        case 'deviceSeries': {
          if (right.identification.type !== 'deviceSeries') {
            return 0;
          }
          return (
            (left.identification.thingieId ?? '').localeCompare(right.identification.thingieId ?? '')
            || left.identification.deviceAddress.localeCompare(right.identification.deviceAddress)
            || left.identification.packetType.localeCompare(right.identification.packetType)
            || left.identification.property.localeCompare(right.identification.property)
          );
        }
        case 'annotationSeries': {
          if (right.identification.type !== 'annotationSeries') {
            return 0;
          }
          return (
            (left.identification.thingieId ?? '').localeCompare(right.identification.thingieId ?? '')
            || Number(left.identification.systemAnnotation) - Number(right.identification.systemAnnotation)
          );
        }
      }
    });
  }

  private getTaskAnchorKey(series: IDataSeries): string | undefined {
    if (series.identification.type !== 'thingieSeries') {
      return undefined;
    }

    return [
      series.identification.thingieId ?? '',
      series.identification.processIndex,
      series.identification.taskIndex
    ].join(':');
  }

  private getSeriesKey(series: IDataSeries): string {
    switch (series.identification.type) {
      case 'thingieSeries':
        return [
          'thingie',
          series.identification.thingieId ?? '',
          series.identification.processIndex,
          series.identification.taskIndex,
          series.identification.output,
          series.identification.offlineData ? 'offline' : 'live'
        ].join(':');
      case 'deviceSeries':
        return [
          'device',
          series.identification.thingieId ?? '',
          series.identification.deviceAddress,
          series.identification.packetType,
          series.identification.property
        ].join(':');
      case 'annotationSeries':
        return [
          'annotation',
          series.identification.thingieId ?? '',
          series.identification.systemAnnotation ? 'system' : 'user'
        ].join(':');
    }
  }
}
